import { useCallback, useEffect, useRef, useState } from 'react';

export const DEFAULT_UNSAVED_CHANGES_MESSAGE = 'You have unsaved changes. Are you sure you want to leave this page?';

/**
 * Blocks navigation away from a dirty form and shows a confirmation prompt.
 *
 * `history.block` (react-router v5 / history v4) covers both in-app PUSH/REPLACE
 * navigation (e.g. a Cancel button calling `history.push`) and browser
 * back/forward (POP) with the same registration, so one guard/one dialog
 * handles every navigation case. Refresh/tab/window close can't go through
 * `history` at all, so those are covered separately via `beforeunload`.
 */
const useUnsavedChangesGuard = ({ hasUnsavedChanges, history, message = DEFAULT_UNSAVED_CHANGES_MESSAGE }) => {
    const [isPromptOpen, setIsPromptOpen] = useState(false);
    const hasUnsavedChangesRef = useRef(hasUnsavedChanges);
    const pendingTransitionRef = useRef(null);
    const unblockRef = useRef(null);
    const bypassNextRef = useRef(false);

    useEffect(() => {
        hasUnsavedChangesRef.current = hasUnsavedChanges;
    }, [hasUnsavedChanges]);

    useEffect(() => {
        if (!hasUnsavedChanges) return undefined;

        const handleBeforeUnload = (event) => {
            event.preventDefault();
            event.returnValue = '';
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [hasUnsavedChanges]);

    useEffect(() => {
        if (!history?.block) return undefined;

        unblockRef.current = history.block((location, action) => {
            if (bypassNextRef.current) {
                bypassNextRef.current = false;
                return true;
            }
            if (!hasUnsavedChangesRef.current) {
                return true;
            }
            pendingTransitionRef.current = { location, action };
            setIsPromptOpen(true);
            return false;
        });

        return () => {
            unblockRef.current?.();
            unblockRef.current = null;
        };
    }, [history]);

    const confirmLeave = useCallback(() => {
        const pending = pendingTransitionRef.current;
        setIsPromptOpen(false);
        pendingTransitionRef.current = null;
        if (!pending || !history) return;

        // Unblock first so replaying the transition isn't blocked again.
        unblockRef.current?.();
        unblockRef.current = null;

        if (pending.action === 'REPLACE') {
            history.replace(pending.location);
        } else {
            history.push(pending.location);
        }
    }, [history]);

    const cancelLeave = useCallback(() => {
        setIsPromptOpen(false);
        pendingTransitionRef.current = null;
    }, []);

    /**
     * For a navigation the caller knows is safe (e.g. redirecting right after a
     * successful save) even though `hasUnsavedChanges` hasn't re-rendered as false yet —
     * state updates from the save are still batched when the redirect fires.
     */
    const bypassNextNavigation = useCallback(() => {
        bypassNextRef.current = true;
    }, []);

    return { isPromptOpen, confirmLeave, cancelLeave, message, bypassNextNavigation };
};

export default useUnsavedChangesGuard;
